import { useProductCatalog } from '@/contexts/useProductCatalog'
import type { ProductDetails } from '@/generated/http-clients/v3ApiSchemas'
import {
  type CollectionItem,
  Field,
  HStack,
  Portal,
  Select,
  Tag,
  Text,
  createListCollection,
  useSelectContext,
} from '@chakra-ui/react'
import { isNil } from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function ProductSelect({
  value,
  onChange,
  dataField = 'product-id',
  required,
  disabled,
  onBlur,
  error,
}: Readonly<{
  value?: number | string
  onChange?: (value?: number) => void
  dataField?: string
  required?: boolean
  disabled?: boolean
  error?: string
  onBlur?: () => void
}>) {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { data, isLoading } = useProductCatalog()
  const [isOpen, setIsOpen] = useState(false)

  const productsCollection = useMemo(
    () =>
      createListCollection({
        items: [...(data || [])]
          .sort(sortProductList)
          .filter(
            (product): product is typeof product & { id: number } =>
              product.id != null,
          )
          .map((product) => ({
            value: String(product.id),
            label: String(product.name),
          })),
      }),
    [data],
  )

  const isInvalid = Boolean(error)

  return (
    <Field.Root
      required={required}
      display="flex"
      alignItems="start"
      invalid={isInvalid}
    >
      <Field.Label>
        {t('fields.productId')}
        <Field.RequiredIndicator />
      </Field.Label>
      <Select.Root
        collection={productsCollection}
        value={value ? [String(value)] : []}
        onValueChange={(e) => {
          const value = e.value[0]
          onChange?.(value ? Number(value) : undefined)
        }}
        onOpenChange={(val) => {
          !val.open && onBlur?.()
          setIsOpen(val.open)
        }}
        positioning={{ sameWidth: true }}
        data-field={dataField}
        disabled={disabled}
        invalid={isInvalid}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger
            backgroundColor="bg"
            borderColor={isInvalid ? 'content.error' : 'border'}
          >
            <SelectValueWithTag
              placeholder={
                isLoading
                  ? String(t('productSelect.loading'))
                  : String(t('productSelect.placeholder'))
              }
            />
          </Select.Trigger>
          {!disabled && (
            <Select.IndicatorGroup>
              <Select.ClearTrigger />
              <Select.Indicator />
            </Select.IndicatorGroup>
          )}
        </Select.Control>
        <Portal>
          <Select.Positioner>
            {/* Display is managed manually because for some reason content node is not unmounted when
              menu is closed, and it blocks content below */}
            <Select.Content display={isOpen ? 'block' : 'none'}>
              {productsCollection.items.map((product) => (
                <Select.Item item={product} key={product.value}>
                  <HStack gap={2}>
                    <Tag.Root size="sm">
                      <Tag.Label>{product.value}</Tag.Label>
                    </Tag.Root>
                    <Text>{product.label}</Text>
                  </HStack>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
      <Field.ErrorText position="absolute" bottom="-1.2rem">
        {error}
      </Field.ErrorText>
    </Field.Root>
  )
}

function SelectValueWithTag({
  placeholder,
}: Readonly<{ placeholder: string }>) {
  const select = useSelectContext()
  const items = select.selectedItems as CollectionItem[]

  return (
    <Select.ValueText placeholder={placeholder}>
      {items.length > 0 ? (
        <HStack gap={2}>
          <Tag.Root size="sm">
            <Tag.Label>{items[0].value}</Tag.Label>
          </Tag.Root>
          <Text>{items[0].label}</Text>
        </HStack>
      ) : null}
    </Select.ValueText>
  )
}

function sortProductList(a: ProductDetails, b: ProductDetails): number {
  if (isNil(a.id) && isNil(b.id)) {
    return 0
  }
  if (isNil(a.id)) {
    return -1
  }
  if (isNil(b.id)) {
    return 1
  }
  return a.id - b.id
}
